<template>
  <div>
    <text-input
      v-if="fullNameRequired"
      v-model="address.addressee"
      type="text"
      autocomplete="name"
      label="Full Name"
      name="addressFullName"
      class="address-name"
      :validations="{
        name: true,
        required: true
      }"
    />

    <autocomplete-input
      v-model="address.line1"
      autocomplete="address-line1"
      label="Street Address"
      name="addressLine1"
      class="address-line-1"
      :loading="loadingLocationData"
      :validations="{
        required: true
      }"
      :values="locations"
      @autocompleted="addressAutocompletedHandler"
      @search-term-input="addressSearchTermInputHandler"
    />

    <text-input
      v-model="address.line2"
      type="text"
      autocomplete="address-line2"
      label="Address Line 2"
      placeholder="Suite or apartment #"
      name="addressLine2"
      class="address-line-2"
    />

    <div class="layout--address-line-3">
      <text-input
        v-model="address.city"
        type="text"
        autocomplete="address-level2"
        label="City"
        name="city"
        :validations="{
          required: true
        }"
      />

      <text-input
        v-model="address.state"
        type="text"
        autocomplete="address-level1"
        label="State"
        name="state"
        :validations="{
          required: true,
          length: 2
        }"
      />

      <text-input
        v-model="address.postalCode"
        type="text"
        autocomplete="postal-code"
        label="Postal Code"
        name="postalCode"
        inputmode="numeric"
        :validations="{
          required: true,
          postal_code: true
        }"
        mask="postalCode"
      />
    </div>
  </div>
</template>



<script>
  import { mapGetters } from 'vuex';
  import debounce from 'debounce';

  import events from '@/services/events';
  import logger from '@/services/logger';


  export default {
    name: 'AddressForm',

    inject: {
      $validator: {
        from: '$validator'
      },

      validatedForm: {
        from: 'validatedForm'
      }
    },

    props: {
      fullNameRequired: {
        type: Boolean,
        default: false
      },
      modelValue: {
        type: Object,
        default() {
          return {};
        }
      }
    },

    data() {
      return {
        address: {
          addressee: null,
          city: null,
          countryCode: 'US',
          line1: null,
          line2: null,
          postalCode: null,
          state: null
        },
        addressSearchTerm: null,
        loadingLocationData: false,
        locations: [],
        selectedGooglePlaceId: null
      };
    },

    computed: {
      ...mapGetters('session', ['currentUser'])
    },

    watch: {
      address: {
        deep: true,
        handler: 'addressChangeHandler'
      },
      selectedGooglePlaceId: 'googlePlaceSelected',
      modelValue: {
        deep: true,
        handler: 'valueChangeHandler',
        immediate: true
      }
    },

    methods: {
      addressAutocompletedHandler(autocompletion) {
        this.googlePlaceSelected(autocompletion.value);
      },

      addressChangeHandler() {
        this.$emit('update:modelValue', this.address);
      },

      addressSearchTermInputHandler(addressSearchTerm) {
        this.addressSearchTerm = addressSearchTerm;

        this.searchGooglePlaces();
      },

      autocompleteAddressWithGooglePlaceData(data) {
        const addressComponents = {};

        data.address_components.forEach((component) => {
          component.types.forEach((type) => {
            if (type === 'street_number') {
              addressComponents.streetNumber = component.long_name;
            }
            else if (type === 'route') {
              addressComponents.streetName = component.long_name;
            }
            else if (type === 'locality') {
              addressComponents.city = component.long_name;
            }
            else if (type === 'administrative_area_level_1') {
              addressComponents.state = component.short_name;
            }
            else if (type === 'postal_code') {
              addressComponents.postalCode = component.long_name;
            }
            else if (type === 'country') {
              addressComponents.country = component.short_name;
            }
          });
        });

        this.address.line1 = addressComponents.streetNumber ? `${addressComponents.streetNumber} ${addressComponents.streetName}` : addressComponents.streetName;
        this.address.city = addressComponents.city;
        this.address.postalCode = addressComponents.postalCode;
        this.address.state = addressComponents.state;

        if (addressComponents.country && addressComponents.country !== 'US') {
          /*
            NOTE: This is due to Google returning Puerto Rico and US Virgin Islands addresses in a strange format.
            If the address is not returned with a US country code, we need to use the country code as the state
            value and the state as the city value.
          */

          this.address.state = addressComponents.country;
          this.address.city = addressComponents.state;
        }
      },

      genericErrorHandler() {
        events.emit('notification', {
          type: 'error',
          message: 'Failed to autocomplete your address. Please fill address in manually.'
        });
      },

      async googlePlaceSelected(placeId) {
        try {
          this.loadingLocationData = true;

          const response = await this.$googleMapsService.getPlaceDetails(placeId);

          this.autocompleteAddressWithGooglePlaceData(response);
        }

        catch (error) {
          logger.error(error);

          this.genericErrorHandler();
        }

        finally {
          this.loadingLocationData = false;
        }
      },

      searchGooglePlaces: debounce(async function searchGooglePlaces() {
        try {
          if (this.addressSearchTerm.length > 1) {
            const predictionType = 'address';

            this.locations = await this.$googleMapsService.getAutoCompletePredictions(this.addressSearchTerm, predictionType);
          }
          else {
            this.locations = [];
          }
        }

        catch (error) {
          logger.error(error);
        }
      }, 500),


      valueChangeHandler(address) {
        this.address = address;

        if (!this.address.addressee && this.currentUser.accountId) {
          this.address.addressee = this.currentUser.fullName;
        }
      }
    }
  };
</script>
